import {
  API_CHANGE_PASSWORD,
  API_LINK_AUTH,
  API_USER,
} from '../constants/keyFactory';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { SHA512 } from 'crypto-js';

export type TUserData = {
  email: string;
  provider: string;
  nickname: string;
  authToken: string;
  stoppedAt: string | null;
};

const useCheckLogin = () =>
  useQuery({
    queryKey: ['LOGIN_CHECK'],
    queryFn: async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/oauth`, {
          withCredentials: true,
        });
        return res.data.data as TUserData;
      } catch {
        return null;
      }
    },
    retry: false,
    refetchInterval: 1000 * 30, // 30초마다 자동으로 세션 확인
    refetchOnWindowFocus: true, // 윈도우 포커스 시 자동으로 다시 확인
    refetchOnReconnect: true, // 네트워크 재연결 시 자동으로 다시 확인
    staleTime: 0, // staleTime을 0으로 설정하여 refetchInterval이 항상 실행되도록 보장
  });

const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axios.get(`${process.env.REACT_APP_API_URL}/oauth/logout`, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['LOGIN_CHECK'] });
      if (typeof window === 'undefined') return;
      window.location.reload();
    },
  });
};

const useSignIn = () =>
  useMutation({
    mutationFn: async (par: { email: string; password: string }) => {
      const res = await axios.post(
        process.env.REACT_APP_API_URL + '/auth/sign-in',
        par,
        {
          withCredentials: true,
        }
      );
      return res.data;
    },
  });

const useSignUp = () =>
  useMutation({
    mutationFn: async (par: {
      email: string;
      password: string;
      nickname: string;
    }) => {
      try {
        const res = await axios.post(
          process.env.REACT_APP_API_URL + '/auth/sign-up',
          par
        );
        return res.data;
      } catch (error) {
        return error.response.data;
      }
    },
  });

const useVerifyEmail = () =>
  useMutation({
    mutationFn: async (email: string) => {
      try {
        const res = await axios.get(
          process.env.REACT_APP_API_URL + `/auth/verify-email?email=${email}`
        );
        return res.data;
      } catch (error) {
        return error.response.data;
      }
    },
  });

const useUpdateNickname = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nickname: string) => {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}${API_USER}`,
        { nickname },
        { withCredentials: true }
      );
      return res.data as TUserData;
    },
    onSuccess: (data, nickname) => {
      queryClient.setQueryData(['user'], (oldData: TUserData | undefined) =>
        oldData ? { ...oldData, nickname } : { ...oldData, nickname }
      );
      queryClient.invalidateQueries({ queryKey: ['LOGIN_CHECK'] });
    },
    onError: error => {
      console.error('Failed to update nickname:', error);
    },
  });
};

const useChangePassword = () => {
  return useMutation({
    mutationFn: async ({
      oldPassword,
      newPassword,
      email,
    }: {
      oldPassword: string;
      newPassword: string;
      email: string;
    }) => {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}${API_CHANGE_PASSWORD}`,
        {
          email: email,
          password: SHA512(oldPassword).toString(),
          newPassword: SHA512(newPassword).toString(),
        },
        { withCredentials: true }
      );
      return res.data;
    },
  });
};

const useRestoreAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}${API_USER}/reactivate`,
        {},
        { withCredentials: true }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.setQueryData(['user'], (oldData: TUserData | undefined) =>
        oldData ? { ...oldData, softDeleted: false, stoppedAt: null } : oldData
      );
    },
    onError: error => {
      console.error('Failed to restore account:', error);
    },
  });
};

const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}${API_USER}`,
        {
          withCredentials: true,
        }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['user'] });
    },
  });
};

const usePostLinkAuth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      return axios.post(`${process.env.REACT_APP_API_URL}${API_LINK_AUTH}`, {
        email,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_LINK_AUTH] });
    },
  });
};

export {
  useCheckLogin,
  useLogout,
  useSignIn,
  useSignUp,
  useVerifyEmail,
  useUpdateNickname,
  useChangePassword,
  useRestoreAccount,
  useDeleteAccount,
  usePostLinkAuth,
};
